import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, MoveRight, RefreshCw, SplitSquareVertical, Mail, X, CheckSquare, Undo2 } from "lucide-react";

export default function ReassignBoard() {
    const [board, setBoard] = React.useState({ grades: [] });
    const [dragging, setDragging] = React.useState(null);
    const [hoverClass, setHoverClass] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [splitting, setSplitting] = React.useState(null);
    // Bulk selection: array of {studentId, fromClassId, grade, name}
    const [selected, setSelected] = React.useState([]);
    const [bulkTarget, setBulkTarget] = React.useState("");
    const [bulkBusy, setBulkBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/reassign-board");
            setBoard(data);
        } finally { setLoading(false); }
    }, []);
    React.useEffect(() => { load(); }, [load]);

    async function reassign(fromClassId, toClassId, studentId) {
        if (fromClassId === toClassId) return;
        try {
            const { data } = await api.post("/admin/reassign", {
                student_id: studentId,
                from_class_id: fromClassId,
                to_class_id: toClassId,
            });
            showUndoToast({
                title: `Moved to ${data.to}`,
                description: data.parent_notified ? "Parent notified by email." : "Move saved.",
                batchId: data.batch_id,
                expiresIn: data.undo_expires_in,
            });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reassign failed");
        }
    }

    async function splitClass(classId) {
        setSplitting(classId);
        try {
            const { data } = await api.post(`/admin/classes/${classId}/duplicate`);
            toast.success(`Created ${data.name}`, {
                description: "You can now drag or bulk-move students into the new section.",
            });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Split failed");
        } finally { setSplitting(null); }
    }

    function toggleSelect(s) {
        setSelected((prev) => {
            const exists = prev.find((x) => x.studentId === s.studentId);
            if (exists) return prev.filter((x) => x.studentId !== s.studentId);
            return [...prev, s];
        });
    }

    function clearSelection() { setSelected([]); setBulkTarget(""); }

    async function runBulk() {
        if (!bulkTarget || selected.length === 0) return;
        const moves = selected.map((s) => ({
            student_id: s.studentId,
            from_class_id: s.fromClassId,
            to_class_id: bulkTarget,
        })).filter((m) => m.from_class_id !== m.to_class_id);
        if (moves.length === 0) {
            toast.info("All selected students are already in the target class.");
            return;
        }
        setBulkBusy(true);
        try {
            const { data } = await api.post("/admin/reassign-bulk", { moves });
            showUndoToast({
                title: `Moved ${data.moved.length} student${data.moved.length === 1 ? "" : "s"}`,
                description: `${data.families_notified} of ${data.total_families} famil${data.total_families === 1 ? "y" : "ies"} notified by email.`,
                batchId: data.batch_id,
                expiresIn: data.undo_expires_in,
            });
            clearSelection();
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk move failed");
        } finally { setBulkBusy(false); }
    }

    async function undoBatch(batchId, toastId) {
        try {
            const { data } = await api.post(`/admin/reassign/undo/${batchId}`);
            toast.dismiss(toastId);
            toast.success(`Undone · ${data.reverted.length} student${data.reverted.length === 1 ? "" : "s"} restored`, {
                description: `${data.families_notified} famil${data.families_notified === 1 ? "y" : "ies"} received a correction email.`,
                icon: <Undo2 className="w-4 h-4" />,
            });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Undo failed");
        }
    }

    function showUndoToast({ title, description, batchId, expiresIn }) {
        const durationMs = (expiresIn || 30) * 1000;
        const toastId = toast(title, {
            description,
            duration: durationMs,
            icon: <Mail className="w-4 h-4" />,
            className: "border-primary/40",
            action: {
                label: "Undo",
                onClick: () => undoBatch(batchId, toastId),
            },
        });
    }

    // Admin override: any class in any grade is a valid destination
    const targetOptions = board.grades.flatMap((g) => g.classes.map((c) => ({ ...c, grade: g.grade })));
    const selectedGrades = [...new Set(selected.map((s) => s.grade))];

    return (
        <div className="pb-32">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="overline text-brand-stone">Admin</div>
                    <h2 className="font-serif text-3xl text-brand-ink">Class reassignment</h2>
                    <p className="text-sm text-brand-stone mt-1 max-w-xl">
                        Drag a student to move one, or tick the checkboxes to bulk-move several in one action.
                        Moving a student into another grade's class also updates their grade.
                        Parents get one email per family with every child included.
                    </p>
                </div>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-2 text-sm text-brand-stone hover:text-primary"
                    data-testid="reassign-refresh">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {board.grades.length === 0 && (
                <Card><CardContent className="p-10 text-center text-brand-stone">
                    No students have been placed in any class yet.
                </CardContent></Card>
            )}

            <div className="space-y-10">
                {board.grades.map((g) => {
                    const multi = true;
                    return (
                        <section key={g.grade} data-testid={`grade-section-${g.grade}`}>
                            <div className="flex items-center gap-3 mb-4">
                                <Badge className="bg-primary/10 text-primary border-0 text-sm px-3 py-1">
                                    Grade {g.grade}
                                </Badge>
                                <div className="text-xs text-brand-stone">
                                    {g.classes.length} class{g.classes.length > 1 ? "es" : ""}

                                </div>
                            </div>
                            <div className={`grid gap-4 grid-cols-1 md:grid-cols-2 ${g.classes.length >= 3 ? "xl:grid-cols-3" : ""}`}>
                                {g.classes.map((c) => {
                                    const isHover = hoverClass === c.class_id;
                                    const canReceive = dragging && dragging.fromClassId !== c.class_id;
                                    const atCap = c.students.length >= c.capacity;
                                    return (
                                        <div
                                            key={c.class_id}
                                            onDragOver={(e) => {
                                                if (canReceive && !atCap) { e.preventDefault(); setHoverClass(c.class_id); }
                                            }}
                                            onDragLeave={() => { if (isHover) setHoverClass(null); }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                if (!canReceive || atCap) return;
                                                if (dragging.grade !== g.grade && !window.confirm(`Move this student from Grade ${dragging.grade} to Grade ${g.grade}? Their grade will be updated.`)) { setHoverClass(null); setDragging(null); return; }
                                                reassign(dragging.fromClassId, c.class_id, dragging.studentId);
                                                setHoverClass(null);
                                                setDragging(null);
                                            }}
                                            data-testid={`class-column-${c.class_id}`}
                                            className={`rounded-2xl border-2 transition-colors bg-card ${isHover && canReceive && !atCap
                                                ? "border-primary bg-primary/5"
                                                : canReceive && !atCap
                                                    ? "border-dashed border-primary/40"
                                                    : "border-border/70"}`}
                                        >
                                            <div className="p-4 border-b border-border/60 flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="font-dash font-semibold text-brand-ink truncate">{c.name}</div>
                                                    <div className="text-xs text-brand-stone mt-0.5 truncate">
                                                        {c.teacher_name || "Teacher pending"} · {c.room || "Room TBD"}
                                                    </div>
                                                </div>
                                                <Badge
                                                    className={`border-0 shrink-0 ${atCap
                                                        ? "bg-destructive/10 text-destructive"
                                                        : "bg-secondary text-brand-ink"}`}>
                                                    <Users className="w-3 h-3 mr-1" />
                                                    {c.students.length}/{c.capacity}
                                                </Badge>
                                            </div>
                                            <div className="p-3 min-h-[80px] space-y-2">
                                                {c.students.length === 0 && (
                                                    <div className="text-xs text-brand-stone italic px-2 py-3 text-center">
                                                        No students in this class.
                                                    </div>
                                                )}
                                                {c.students.map((s) => {
                                                    const isSel = !!selected.find((x) => x.studentId === s.student_id);
                                                    return (
                                                        <div
                                                            key={s.student_id}
                                                            draggable={multi && !isSel}
                                                            onDragStart={() => setDragging({
                                                                studentId: s.student_id,
                                                                fromClassId: c.class_id,
                                                                grade: g.grade,
                                                            })}
                                                            onDragEnd={() => { setDragging(null); setHoverClass(null); }}
                                                            data-testid={`student-card-${s.student_id}`}
                                                            className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${isSel
                                                                ? "border-primary bg-primary/5"
                                                                : "border-border/70 bg-background"} ${multi
                                                                ? (isSel ? "cursor-pointer" : "cursor-grab active:cursor-grabbing hover:border-primary/40 hover:shadow-sm")
                                                                : "opacity-70"} ${dragging?.studentId === s.student_id ? "opacity-40" : ""}`}
                                                        >
                                                            {multi && (
                                                                <Checkbox
                                                                    checked={isSel}
                                                                    onCheckedChange={() => toggleSelect({
                                                                        studentId: s.student_id,
                                                                        fromClassId: c.class_id,
                                                                        grade: g.grade,
                                                                        name: `${s.first_name} ${s.last_name}`,
                                                                    })}
                                                                    data-testid={`select-${s.student_id}`}
                                                                />
                                                            )}
                                                            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-dash font-semibold">
                                                                {s.first_name[0]}{s.last_name[0]}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="text-sm font-medium truncate">
                                                                    {s.first_name} {s.last_name}
                                                                </div>
                                                            </div>
                                                            {multi && !isSel && <MoveRight className="w-3 h-3 text-brand-stone/40" />}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="border-t border-border/60 p-3">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    disabled={splitting === c.class_id}
                                                    onClick={() => splitClass(c.class_id)}
                                                    data-testid={`split-btn-${c.class_id}`}
                                                    className="w-full gap-2 text-xs">
                                                    <SplitSquareVertical className="w-3 h-3" />
                                                    {splitting === c.class_id ? "Splitting…" : "Split into a new section"}
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}
            </div>

            {selected.length > 0 && (
                <div
                    data-testid="bulk-action-bar"
                    className="fixed bottom-0 inset-x-0 z-40 bg-primary text-primary-foreground shadow-2xl animate-fade-up"
                >
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-3 shrink-0">
                            <CheckSquare className="w-5 h-5" />
                            <div className="font-dash text-sm">
                                <div className="font-semibold">{selected.length} selected</div>
                                <div className="opacity-75 text-xs">Grade {selectedGrades.join(", ")}</div>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 max-h-16 overflow-y-auto">
                            {selected.slice(0, 6).map((s) => (
                                <span key={s.studentId} className="text-xs bg-white/15 rounded-full px-3 py-1">{s.name}</span>
                            ))}
                            {selected.length > 6 && (
                                <span className="text-xs bg-white/15 rounded-full px-3 py-1">+{selected.length - 6} more</span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Select value={bulkTarget} onValueChange={setBulkTarget}>
                                <SelectTrigger
                                    data-testid="bulk-target-select"
                                    className="w-56 bg-white/10 border-white/30 text-primary-foreground">
                                    <SelectValue placeholder="Move to…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {targetOptions.map((c) => {
                                        const willFit = c.students.length + selected.filter((s) => s.fromClassId !== c.class_id).length <= c.capacity;
                                        return (
                                            <SelectItem key={c.class_id} value={c.class_id} disabled={!willFit} data-testid={`bulk-target-${c.class_id}`}>
                                                Grade {c.grade} · {c.name} ({c.students.length}/{c.capacity}){!willFit ? " · full" : ""}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                            <Button
                                data-testid="bulk-move-btn"
                                onClick={runBulk}
                                disabled={!bulkTarget || bulkBusy}
                                className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2">
                                <Mail className="w-4 h-4" />
                                {bulkBusy ? "Moving…" : `Move ${selected.length}`}
                            </Button>
                            <button
                                onClick={clearSelection}
                                data-testid="bulk-clear-btn"
                                className="p-2 rounded-lg text-primary-foreground/80 hover:bg-white/10">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
